import { PROVINCIES, type Museum, type Provincie } from '../data/types';

/**
 * Gebruikersgegevens: per museum-ID alleen wat afwijkt van "niets gemarkeerd".
 * Compact gehouden, want het staat in localStorage en (deels) in deellinks.
 *   b: bezoekdata (ISO 'YYYY-MM-DD', oudste eerst). Bezocht = minstens één datum.
 *   w: op de wensenlijst
 *   f: favoriet
 */
export interface MuseumStatus {
  b?: string[];
  w?: true;
  f?: true;
}
export type StatusMap = Record<string, MuseumStatus>;

export interface KaartInfo {
  /** Prijs van je Museumkaart in eurocent. */
  prijs: number;
  /** Ingangsdatum van je huidige kaart (ISO): gekocht of voor het eerst gebruikt. null = nog niet ingevuld. */
  vanaf: string | null;
}

const ISO_DATUM = /^\d{4}-\d{2}-\d{2}$/;

/** Vandaag als 'YYYY-MM-DD' in de lokale tijd (niet UTC: na middernacht is het al een nieuwe dag). */
export const vandaag = (nu = new Date()) =>
  `${nu.getFullYear()}-${String(nu.getMonth() + 1).padStart(2, '0')}-${String(nu.getDate()).padStart(2, '0')}`;

export function standaardKaart(): KaartInfo {
  return { prijs: 7500, vanaf: null };
}

/** Parser voor de opgeslagen status: ongeldige onderdelen worden weggelaten, de rest blijft. */
export function parseStatusMap(raw: unknown): StatusMap | undefined {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return undefined;
  const out: StatusMap = {};
  for (const [id, v] of Object.entries(raw as Record<string, unknown>)) {
    if (!v || typeof v !== 'object') continue;
    const { b, w, f } = v as Record<string, unknown>;
    const s: MuseumStatus = {};
    if (Array.isArray(b)) {
      const data = b.filter((d): d is string => typeof d === 'string' && ISO_DATUM.test(d)).sort();
      if (data.length) s.b = data;
    }
    if (w === true) s.w = true;
    if (f === true) s.f = true;
    if (s.b || s.w || s.f) out[id] = s;
  }
  return out;
}

export function parseKaartInfo(raw: unknown): KaartInfo | undefined {
  if (!raw || typeof raw !== 'object') return undefined;
  const { prijs, vanaf } = raw as Record<string, unknown>;
  if (typeof prijs !== 'number' || !Number.isFinite(prijs) || prijs < 0 || prijs > 100_000) return undefined;
  if (vanaf !== undefined && vanaf !== null && (typeof vanaf !== 'string' || !ISO_DATUM.test(vanaf))) return undefined;
  return { prijs: Math.round(prijs), vanaf: typeof vanaf === 'string' ? vanaf : null };
}

const opruimen = (map: StatusMap, id: string, s: MuseumStatus): StatusMap => {
  const next = { ...map };
  if (s.b?.length || s.w || s.f) next[id] = s;
  else delete next[id];
  return next;
};

export const isBezocht = (s?: MuseumStatus) => !!s?.b?.length;

/** Bezocht aan/uit. Aan = één bezoek vandaag; uit = alle bezoeken weg (de UI biedt ongedaan maken). */
export function toggleBezocht(map: StatusMap, id: string, datum = vandaag()): StatusMap {
  const s = map[id] ?? {};
  return opruimen(map, id, { ...s, b: isBezocht(s) ? undefined : [datum] });
}

export function voegBezoekToe(map: StatusMap, id: string, datum = vandaag()): StatusMap {
  const s = map[id] ?? {};
  return opruimen(map, id, { ...s, b: [...(s.b ?? []), datum].sort() });
}

export function toggleVlag(map: StatusMap, id: string, vlag: 'w' | 'f'): StatusMap {
  const s = { ...(map[id] ?? {}) };
  if (s[vlag]) delete s[vlag];
  else s[vlag] = true;
  return opruimen(map, id, s);
}

/** Na een eerste bezoek verdwijnt een museum van de wensenlijst (doel bereikt). */
export function markeerBezocht(map: StatusMap, id: string, datum = vandaag()): StatusMap {
  const s = map[id] ?? {};
  if (isBezocht(s)) return toggleBezocht(map, id);
  const { w: _w, ...rest } = s;
  return opruimen(map, id, { ...rest, b: [datum] });
}

// ---------- statistieken ----------

export interface ProvincieVoortgang {
  provincie: Provincie;
  bezocht: number;
  totaal: number;
}

export interface Voortgang {
  totaal: number;
  bezocht: number;
  wensen: number;
  favorieten: number;
  bezoeken: number;
  /** Bespaard sinds de ingangsdatum van je kaart, in eurocent. */
  bespaard: number;
  /** Bezoeken in de kaartperiode zonder bekende prijs (tellen niet mee in `bespaard`). */
  zonderPrijs: number;
  perProvincie: ProvincieVoortgang[];
}

/** Vanaf wanneer bezoeken meetellen: de ingangsdatum, of zolang die ontbreekt de laatste 12 maanden. */
export function effectieveStart(kaart: KaartInfo, nu = new Date()): string {
  if (kaart.vanaf) return kaart.vanaf;
  const d = new Date(nu.getFullYear() - 1, nu.getMonth(), nu.getDate() + 1);
  return vandaag(d);
}

export function berekenVoortgang(musea: Museum[], map: StatusMap, kaart: KaartInfo): Voortgang {
  const perProv = new Map<Provincie, ProvincieVoortgang>(
    PROVINCIES.map((p) => [p, { provincie: p, bezocht: 0, totaal: 0 }]),
  );
  const start = effectieveStart(kaart);
  let bezocht = 0, wensen = 0, favorieten = 0, bezoeken = 0, bespaard = 0, zonderPrijs = 0;
  for (const m of musea) {
    const s = map[m.id];
    const p = perProv.get(m.provincie)!;
    p.totaal++;
    if (s?.w) wensen++;
    if (s?.f) favorieten++;
    if (!isBezocht(s)) continue;
    bezocht++;
    p.bezocht++;
    bezoeken += s!.b!.length;
    const inPeriode = s!.b!.filter((d) => d >= start).length;
    if (m.prijs) bespaard += inPeriode * m.prijs;
    else zonderPrijs += inPeriode;
  }
  return {
    totaal: musea.length,
    bezocht,
    wensen,
    favorieten,
    bezoeken,
    bespaard,
    zonderPrijs,
    perProvincie: [...perProv.values()].filter((p) => p.totaal > 0),
  };
}

// ---------- badges ----------

export type BadgeId =
  | 'eerste' | 'tien' | 'vijfentwintig' | 'vijftig' | 'honderd'
  | 'drieProvincies' | 'heelNederland' | 'provincieCompleet'
  | 'kunstkenner' | 'tijdreiziger' | 'terugverdiend' | 'trouweBezoeker';

export interface Badge {
  id: BadgeId;
  behaald: boolean;
  /** Voortgang richting de badge, 0..1 */
  voortgang: number;
}

export function berekenBadges(musea: Museum[], map: StatusMap, v: Voortgang, kaart: KaartInfo): Badge[] {
  const bezochte = musea.filter((m) => isBezocht(map[m.id]));
  const provincies = new Set(bezochte.map((m) => m.provincie)).size;
  const telCat = (c: Museum['categorie']) => bezochte.filter((m) => m.categorie === c).length;
  const besteProvincie = Math.max(0, ...v.perProvincie.map((p) => p.bezocht / p.totaal));
  const vaakst = Math.max(0, ...bezochte.map((m) => map[m.id].b!.length));
  const doel = (n: number, totaal: number) => Math.min(1, totaal > 0 ? n / totaal : 0);

  const lijst: [BadgeId, number][] = [
    ['eerste', doel(v.bezocht, 1)],
    ['tien', doel(v.bezocht, 10)],
    ['vijfentwintig', doel(v.bezocht, 25)],
    ['vijftig', doel(v.bezocht, 50)],
    ['honderd', doel(v.bezocht, 100)],
    ['drieProvincies', doel(provincies, 3)],
    ['heelNederland', doel(provincies, 12)],
    ['provincieCompleet', besteProvincie],
    ['kunstkenner', doel(telCat('kunst'), 10)],
    ['tijdreiziger', doel(telCat('geschiedenis'), 10)],
    ['terugverdiend', doel(v.bespaard, kaart.prijs)],
    ['trouweBezoeker', doel(vaakst, 3)],
  ];
  return lijst
    // Badges die met deze dataset onhaalbaar zijn (bv. 100 musea in de voorbeeldset) tonen we niet.
    .filter(([id]) => !(id === 'honderd' && musea.length < 100) && !(id === 'vijftig' && musea.length < 50))
    .map(([id, voortgang]) => ({ id, voortgang, behaald: voortgang >= 1 }));
}

// ---------- afstand ----------

/** Afstand in km (haversine). */
export function afstandKm(a: { lat: number; lng: number }, b: { lat: number; lng: number }): number {
  const rad = (x: number) => (x * Math.PI) / 180;
  const dLat = rad(b.lat - a.lat);
  const dLng = rad(b.lng - a.lng);
  const h = Math.sin(dLat / 2) ** 2 + Math.cos(rad(a.lat)) * Math.cos(rad(b.lat)) * Math.sin(dLng / 2) ** 2;
  return 2 * 6371 * Math.asin(Math.sqrt(h));
}

// ---------- zoeken ----------

export const zoekSleutel = (s: string) =>
  s.toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '').replace(/['’]/g, '');

/** Eenvoudige zoekfunctie op naam en plaats; woorden mogen in elke volgorde. Naam-treffers eerst. */
export function zoekMusea(musea: Museum[], query: string, max = 8): Museum[] {
  const woorden = zoekSleutel(query).split(/\s+/).filter(Boolean);
  if (!woorden.length) return [];
  return musea
    .map((m) => {
      const naam = zoekSleutel(m.naam);
      const alles = `${naam} ${zoekSleutel(m.plaats)}`;
      if (!woorden.every((w) => alles.includes(w))) return null;
      const score = (naam.startsWith(woorden[0]) ? 0 : 1) + (woorden.every((w) => naam.includes(w)) ? 0 : 1);
      return { m, score };
    })
    .filter((x): x is { m: Museum; score: number } => x !== null)
    .sort((a, b) => a.score - b.score || a.m.naam.localeCompare(b.m.naam, 'nl'))
    .slice(0, max)
    .map((x) => x.m);
}

// ---------- links ----------

/** Navigatie via Google Maps (extern). Naam + adres vindt de juiste ingang; coördinaten als terugval. */
/** Het museum zelf tonen in Google Maps (extern), zonder route. */
export function googleMapsZoekUrl(m: Museum): string {
  const doel = m.adres ? `${m.naam}, ${m.adres}, ${m.plaats}` : `${m.lat},${m.lng}`;
  return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(doel)}`;
}

export function googleMapsUrl(m: Museum): string {
  const doel = m.adres ? `${m.naam}, ${m.adres}, ${m.plaats}` : `${m.lat},${m.lng}`;
  return `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(doel)}`;
}

export const euro = (cent: number, taal: string) =>
  new Intl.NumberFormat(taal === 'en' ? 'en-NL' : 'nl-NL', {
    style: 'currency',
    currency: 'EUR',
    maximumFractionDigits: cent % 100 === 0 ? 0 : 2,
  }).format(cent / 100);
